<template>
  <div>
    <TableVisualizationLayout :leftAction="leftAction" :showTable="false">
      <template slot="headerContent">
        <div v-if="loading" class="custom-card pb-5">
          <div class="mt-3">
            <b-skeleton width="85%" height="40px"></b-skeleton>
          </div>
          <div class="mt-2">
            <b-skeleton width="150px" height="20px"></b-skeleton>
          </div>
          <div class="mt-5">
            <b-skeleton width="100%" height="24px"></b-skeleton>
          </div>
          <div class="mt-5 mb-5">
            <b-skeleton-img no-aspect height="400px"></b-skeleton-img>
          </div>
          <div class="mt-5">
            <b-skeleton width="100%"></b-skeleton>
            <b-skeleton width="100%"></b-skeleton>
            <b-skeleton width="100%"></b-skeleton>
            <b-skeleton width="80%"></b-skeleton>
          </div>
        </div>

        <div v-else-if="error" class="custom-card pb-5 text-center py-5">
          <p class="text-danger">{{ error }}</p>
          <b-button variant="outline-primary" @click="fetchContent">Tentar novamente</b-button>
        </div>

        <div class="custom-card pb-5" v-else-if="content">
          <div class="title mt-3">
            {{ content.title }}
          </div>

          <div v-if="isArticle">
            <div class="author">
              <div><img class="image photo-author" :src="authorImage" /></div>
              <div class="author-name">{{ content.authorName }}, {{ content.specialtyAuthor }} </div>
            </div>
            <div class="date mt-2 date-time">
              <div>{{ formatDate(content.createdAt) }}</div>
              <div>Tempo de leitura: 5 minutos</div>
            </div>
          </div>


          <div class="resume mt-5" v-if="content.coverCallout">
            {{ content.coverCallout }}
          </div>
          <div class="container-capa mt-5" v-if="content.coverImageUrl && !manualUrl">
            <b-skeleton-img v-if="!coverImageSrc" no-aspect height="400px"></b-skeleton-img>
            <img v-else class="image" :src="coverImageSrc" />
          </div>

          <div class="image-description" v-if="isArticle">Descrição da imagem</div>

          <PreviewPdfManual v-if="content.attachmentsManuals && manualUrl" :pdfUrl="manualUrl" />

          <div class="html-text mt-5" v-html="processedHtml"></div>

          <div class="mt-5 d-flex flex-wrap justify-content-center gap-3"
            v-if="content.actionButtons && content.actionButtons.length">
            <div v-for="(btn, index) in content.actionButtons" :key="index">
              <button type="button" class="btn custom-btn-outline btn-sm" @click="openExternalLink(btn.link)">
                {{ btn.texto }}
              </button>
            </div>
          </div>

          <div class="category mt-5" v-if="content.category">
            Categoria:
            <div class="badge-category">
              {{ content.category }}
            </div>
          </div>

        </div>


        <RelatedContent v-if="content && content.id" :content-id="content.id" />
      </template>
    </TableVisualizationLayout>
    <Footer />
  </div>
</template>

<script>
import TableVisualizationLayout from "../../../layouts/TableVisualizationLayout.vue";
import RoutesPaths from "../../../router/router-structure/routes-paths";
import Footer from "../../../layouts/Footer.vue";
import RelatedContent from "../components/RelatedContent.vue";
import ConnectionService from "../../../services/connection/ConnectionService";
import coverImageMixin from "../mixins/coverImageMixin";
import PreviewPdfManual from "../components/PreviewPdfManual.vue";
import { ContentCategory } from "../enums/ContentCategory.js"

export default {
  components: {
    TableVisualizationLayout,
    Footer,
    RelatedContent,
    PreviewPdfManual
  },
  mixins: [coverImageMixin],
  watch: {
    "$route.params.id": {
      immediate: false, // opcional, já chama no mounted
      handler() {
        this.fetchContent();
      },
    },
  },
  async mounted() {
    await this.fetchContent();
  },
  data() {
    return {
      leftAction: {
        text: "Pré-visualização de conteúdo",
        onClick: this.leftActionClickHandler,
      },
      content: null,
      coverImageSrc: null,
      authorImage: null,
      manualUrl: null,
      loading: false,
      error: null,
      processedHtml: '',
      ContentCategory,
    };
  },
  computed: {
    isArticle() {
      return this.content && this.content.category === ContentCategory.ARTICLES;
    }
  },
  methods: {
    openExternalLink(url) {
      console.log(url);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    leftActionClickHandler() {
      this.$router.push({
        path: RoutesPaths.connection.rootName(),
        replace: true,
      });
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    async fetchContent() {
      this.loading = true;
      this.error = null;
      this.coverImageSrc = null;
      this.manualUrl = null;
      this.processedHtml = '';

      try {
        this._revokeCoverImages();
        const id = this.$route.params.id;
        if (id) {
          this.content = await ConnectionService.getById(id);

          if (!this.content) {
            this.error = "Conteúdo não encontrado.";
          } else {
            const tasks = [];
            // Imagem de Capa
            tasks.push(this.loadCoverImage(this.content.coverImageUrl)
              .then(src => this.coverImageSrc = src));

            tasks.push(this.loadCoverImage(this.content.authorImageUrl)
              .then(src => this.authorImage = src));


            // Manual PDF
            const manualAttachment = this.content.attachmentsManuals?.[0];
            if (manualAttachment?.url) {
              tasks.push(this.loadCoverImage(manualAttachment.url)
                .then(src => this.manualUrl = src));
            }

            tasks.push(this.processHtmlImages(this.content.htmlContent)
              .then(html => this.processedHtml = html));

            await Promise.all(tasks);

          }
        } else {
          this.error = "ID do conteúdo não informado.";
        }
      } catch (error) {
        console.error("Erro ao buscar conteúdo", error);
        this.error =
          error?.response?.data?.message ||
          "Não foi possível carregar o conteúdo. Tente novamente.";
      } finally {
        this.loading = false;
        setTimeout(() => {
          this.scrollToTop();
        }, 300);
      }
    },
    async processHtmlImages(htmlString) {
      if (!htmlString) return '';

      // 1. Transforma a string em um documento DOM manipulável
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      const images = doc.querySelectorAll('img');

      // 2. Cria uma lista de promessas para carregar cada imagem
      const promises = Array.from(images).map(async (img) => {
        const originalSrc = img.getAttribute('src');

        // Evita processar se não houver src ou se já for um blob/base64
        if (originalSrc && !originalSrc.startsWith('blob:') && !originalSrc.startsWith('data:')) {
          try {
            // Usa o método do seu mixin para carregar a imagem com tratamento
            const newSrc = await this.loadCoverImage(originalSrc);
            img.setAttribute('src', newSrc);
          } catch (e) {
            console.error("Erro ao carregar imagem do HTML:", originalSrc, e);
          }
        }
      });

      // 3. Aguarda todas as substituições
      await Promise.all(promises);

      // 4. Retorna o HTML modificado como string
      return doc.body.innerHTML;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'auto' });

      const titleEl = this.$el.querySelector('.title');
      if (titleEl) {
        titleEl.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }
  },
};
</script>

<style scoped>
.custom-card {
  width: 100%;
  border: none;
  padding: 0px 16px;
  border-radius: 16px;
  background: #fff;
}

.title {
  color: #00524d;
  font-family: "Trebuchet MS";
  font-size: 32px;
}

.date {
  color: #64696a;
  font-family: "Trebuchet MS";
  font-size: 16px;
}

.resume {
  color: #343a40;
  font-family: "Trebuchet MS";
  font-size: 20px;
  font-weight: 700;
}

.container-capa {
  max-width: 600px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  overflow: hidden;
}

.image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

.html-text {
  color: #343a40;
  font-family: "Trebuchet MS";
  font-size: 16px;
  line-height: 1.5;
}

.html-text ::v-deep p {
  font-size: 16px;
  margin-bottom: 1rem;
}

.html-text ::v-deep h1 {
  font-size: 2rem !important;
  font-weight: 700 !important;
  margin: 1rem 0 !important;
  display: block !important;
  color: #0f172a !important;
}

.html-text ::v-deep h2 {
  font-size: 1.6rem !important;
  font-weight: 600 !important;
  margin: 1rem 0 !important;
  display: block !important;
}

.html-text ::v-deep h3 {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  margin: 0.8rem 0 !important;
  display: block !important;
}

.category {
  color: #343a40;
  font-family: "Trebuchet MS";
  font-size: 20px;
  font-weight: 700;

  display: flex;
  align-items: center;
  gap: 8px;
}

.badge-category {
  border-radius: 4px;
  background: #e9edee;
  padding: 3px 8px;
  color: #212529;
  font-family: "Trebuchet MS";
  font-size: 18px;
  font-weight: 400;

  display: inline-block;
}

.gap-3 {
  gap: 15px;
}

.custom-btn-outline {
  color: #00524D;
  text-align: center;
  font-family: "Trebuchet MS";
  font-size: 16px;
  border-radius: 4px;
  border: 1px solid #00524D;
}

.image-description {
  color: #343A40;
  font-family: "Trebuchet MS";
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 19.2px;
  margin-top: 16px;
}

.author {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 23px 0;
}

.photo-author {
  width: 80px;
  height: 80px;
  aspect-ratio: 1/1;
  border-radius: 100px;
}

.author-name {
  color: #64696A;
  font-family: "Trebuchet MS";
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
}

.date-time {
  display: flex;
  gap: 16px;
  color: #64696A;
  font-family: "Trebuchet MS";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 19.2px;
}
</style>
<style>
/* --- LIMPEZA DE CONTAINER E FLOATS --- */
.html-text {
  display: block;
  position: relative;
}

.html-text::after {
  content: "";
  display: table;
  clear: both;
}

/* Evita que o h1/h2 perca altura ao contornar o vídeo flutuante */
.html-text h1,
.html-text h2,
.html-text h3,
.html-text p {
  clear: none;
  overflow: visible !important;
}

/* --- ESTILOS DE IMAGEM --- */
.html-text .image {
  margin: 1em 0;
  display: table;
  clear: both;
}

.html-text .image-style-align-center,
.html-text .image-style-block-align-center {
  margin-left: auto !important;
  margin-right: auto !important;
  display: table !important;
  text-align: center;
}

.html-text .image-style-align-right {
  float: right !important;
  margin-left: 1.5em !important;
  margin-right: 0 !important;
  display: table;
}

.html-text .image-style-align-left {
  float: left !important;
  margin-right: 1.5em !important;
  margin-left: 0 !important;
  display: table;
}

.html-text img {
  max-width: 100% !important;
  height: auto !important;
}

.html-text figure {
  max-width: 100%;
}

.html-text figcaption {
  font-size: 0.8em;
  font-style: italic;
  text-align: center;
  background: #f8f9fa;
  padding: 5px;
  color: #666;
  display: table-caption;
  caption-side: bottom;
}

/* ========================================================
   --- REGRAS DE ALINHAMENTO DO VÍDEO DO YOUTUBE ---
   ======================================================== */

.html-text div[data-youtube-video] {
  display: block;
  max-width: 100%;
  margin-top: 1rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
}

.html-text div[data-youtube-video] iframe {
  display: block;
  width: 100% !important;
  height: auto !important;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  border: none;
}

/* Alinhamento: Esquerda */
.html-text div[data-youtube-video][data-align="left"] {
  margin-left: 0 !important;
  margin-right: auto !important;
  width: 560px !important;
  max-width: 100% !important;
  float: none !important;
  clear: both;
}

/* Alinhamento: Centralizado */
.html-text div[data-youtube-video][data-align="center"] {
  margin-left: auto !important;
  margin-right: auto !important;
  width: 560px !important;
  max-width: 100% !important;
  float: none !important;
  clear: both;
}

/* Alinhamento: Direita */
.html-text div[data-youtube-video][data-align="right"] {
  margin-left: auto !important;
  margin-right: 0 !important;
  width: 560px !important;
  max-width: 100% !important;
  float: none !important;
  clear: both;
}

/* Flutuante à Esquerda (Texto ao lado) */
.html-text div[data-youtube-video][data-align="float-left"] {
  float: left !important;
  margin: 6px 20px 14px 0 !important;
  width: 420px !important;
  max-width: 50% !important;
  clear: none !important;
}

/* Flutuante à Direita (Texto ao lado) */
.html-text div[data-youtube-video][data-align="float-right"] {
  float: right !important;
  margin: 6px 0 14px 20px !important;
  width: 420px !important;
  max-width: 50% !important;
  clear: none !important;
}
</style>